import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { pushOverlay } from "../shortcuts/overlayStack.js";

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The app's one overlay: a bottom sheet on phones, a centred dialog from 768px.
 * Esc closes the topmost open sheet only; focus moves into the sheet and back to the trigger on close.
 *
 * variant="panel" renders the same header + content inline (no backdrop, portal or scroll lock),
 * for the desktop split view's detail pane.
 */
export function BottomSheet({
  isOpen,
  onClose,
  title,
  children,
  size = "md",
  className = "",
  variant = "sheet",
  closeOnBackdropClick = undefined,
}) {
  const isPanel = variant === "panel";
  const sheetRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const titleId = useId();
  const mouseDownTargetRef = useRef(null);

  const handleBackdropMouseDown = (e) => {
    mouseDownTargetRef.current = e.target;
  };

  const handleBackdropClick = (e) => {
    // Only proceed if mouse down and click release both happened directly on the backdrop
    if (e.target !== e.currentTarget || mouseDownTargetRef.current !== e.currentTarget) {
      return;
    }

    const isDesktop = typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches;
    const canClose = closeOnBackdropClick ?? !isDesktop;

    if (canClose) {
      onClose?.();
    }
  };

  useEffect(() => {
    if (isPanel) return undefined;
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.classList.add("modal-open");
    } else {
      document.body.style.overflow = "";
      document.body.classList.remove("modal-open");
    }
    return () => {
      document.body.style.overflow = "";
      document.body.classList.remove("modal-open");
    };
  }, [isOpen, isPanel]);

  useEffect(() => {
    if (!isOpen || isPanel) return undefined;
    const previouslyFocused = document.activeElement;
    const popOverlay = pushOverlay(() => onCloseRef.current?.());
    // Focus the sheet itself (not the first input) so phones don't pop the keyboard open
    sheetRef.current?.focus({ preventScroll: true });
    return () => {
      popOverlay();
      if (previouslyFocused && document.contains(previouslyFocused)) previouslyFocused.focus?.({ preventScroll: true });
    };
  }, [isOpen, isPanel]);

  // Keep Tab inside the sheet
  const handleKeyDown = (e) => {
    if (e.key !== "Tab" || !sheetRef.current) return;
    const focusable = [...sheetRef.current.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === sheetRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  if (!isOpen) return null;

  if (isPanel) {
    return (
      <section className={`detail-panel ${className}`} aria-labelledby={titleId}>
        <div className="sheet-header">
          <h3 className="sheet-title" id={titleId}>{title}</h3>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Close" title="Close (Esc)">
            ✕
          </button>
        </div>
        <div className="sheet-content">
          {children}
        </div>
      </section>
    );
  }

  return createPortal(
    <div
      className={`modal-backdrop ${className ? `${className}-backdrop` : ""}`}
      onMouseDown={handleBackdropMouseDown}
      onClick={handleBackdropClick}
    >
      <div
        ref={sheetRef}
        className={`bottom-sheet bottom-sheet-${size} ${className}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet-handle" />
        <div className="sheet-header">
          <h3 className="sheet-title" id={titleId}>{title}</h3>
          <button
            type="button"
            className="btn-icon"
            onClick={onClose}
            aria-label="Close"
            title="Close (Esc)"
          >
            ✕
          </button>
        </div>
        <div className="sheet-content">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default BottomSheet;
